import { Box, CircularProgress, IconButton } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MultiSelectDropdown from "./MultiSelectDropdown";
import { getMasterData } from "Samarth Module/api/api";
import { getAlertConfig } from "Samarth Module/constants/alertConfig";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const ChatFilter = ({ formValues: formValuesProp, setFormValues, onClose }) => {
  const formValues = formValuesProp || {};
  const [filterData, setFilterData] = useState({});
  const [loadingFilter, setLoadingFilter] = useState(false);
  const scrollRef = useRef(null);
  const [scrollState, setScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: false,
    thumbLeft: 0,
    thumbWidth: 100,
  });

  const handleChange = (label, values) => {
    setFormValues((prev) => ({ ...prev, [label]: values }));
  };

  const updateFilterData = (data) => {
    const filterMapping = {
      "Business Group": data.BG,
      Format: data.Format,
      "Sub format": data["Sub format"],
      Category: data["Small C"],
      Brand: data["Brand"],
      "Site Cluster": data["Site cluster"],
      "Base Pack": data.Basepack,
    };
    setFilterData(filterMapping);
  };

  const alertType = useMemo(() => ["RMPM Alert"], []);
  const { uiConfig } = useMemo(() => getAlertConfig(alertType), [alertType]);
  const userInfo = useMemo(
    () => JSON.parse(localStorage.getItem("UserInfo"))?.user_info,
    [],
  );
  const [viewType] = useState(
    uiConfig.showToogleButton ? uiConfig.toggleBtn[0] : "",
  );

  const fetchMasterData = async () => {
    setLoadingFilter(true);
    try {
      const response = await getMasterData(
        viewType === "MAI View"
          ? "MAI"
          : viewType === "FG Coverage"
            ? "FG Coverage"
            : alertType,
        userInfo.email,
      );

      if (response) {
        updateFilterData(response.filters);
      }
    } catch (error) {
      console.error("Error fetching master data:", error);
    } finally {
      setLoadingFilter(false);
    }
  };

  useEffect(() => {
    fetchMasterData();
  }, []);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScroll = scrollWidth - clientWidth;
    const trackWidth = 120;
    const thumbWidth = maxScroll > 0
      ? Math.max(30, (clientWidth / scrollWidth) * trackWidth)
      : trackWidth;
    const thumbLeft = maxScroll > 0
      ? (scrollLeft / maxScroll) * (trackWidth - thumbWidth)
      : 0;

    setScrollState({
      canScrollLeft: scrollLeft > 1,
      canScrollRight: scrollLeft < maxScroll - 1,
      thumbLeft,
      thumbWidth,
    });
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener("scroll", updateScrollState);
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      observer.disconnect();
    };
  }, [updateScrollState, filterData]);

  const scroll = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollAmount = 220;
    el.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  if (loadingFilter) {
    return (
      <Box
        sx={{
          py: 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress size={20} sx={{ color: "#0066CC" }} />
      </Box>
    );
  }

  const showScrollControls =
    scrollState.canScrollLeft || scrollState.canScrollRight;

  return (
    <Box
      sx={{
        // background: "linear-gradient(180deg, #E8F1FA 0%, #FFFFFF 100%)",
        borderRadius: "24px 24px 0 0",
        position: "relative",
        zIndex: 10,
        // pb: showScrollControls ? 1 : 0,
      }}
    >
      {/* Scrollable filter row */}
      <Box
        ref={scrollRef}
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: { xs: 1, md: 1.5, lg: 2 },
          px: { xs: 2, lg: 3 },
          py: 2,
          width: "100%",
          boxSizing: "border-box",
          overflowX: "auto",
          flexWrap: "nowrap",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        {Object.entries(filterData).map(([label, options]) => (
          <MultiSelectDropdown
            key={label}
            label={label}
            options={options || []}
            onChange={(values) => handleChange(label, values)}
            formValues={formValues[label] || []}
          />
        ))}
      </Box>

      {/* Scroll navigation: < ——— > */}
      {showScrollControls && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            pb: 1,
          }}
        >
          {/* Left arrow */}
          <IconButton
            onClick={() => scroll("left")}
            disabled={!scrollState.canScrollLeft}
            sx={{
              width: {
                sm: 24,
                md: 28,
                lg: 32,
              },
              height: {
                sm: 24,
                md: 28,
                lg: 32,
              },
              border: "1px solid",
              borderColor: scrollState.canScrollLeft ? "#0066CC" : "#D0D5DD",
              color: scrollState.canScrollLeft ? "#0066CC" : "#D0D5DD",
              "&:hover": {
                backgroundColor: scrollState.canScrollLeft
                  ? "#EAF2FF"
                  : "transparent",
              },
              "&.Mui-disabled": {
                color: "#D0D5DD",
                borderColor: "#D0D5DD",
              },
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: { md: 16, lg: 20 } }} />
          </IconButton>

          {/* Scroll track with thumb */}
          <Box
            sx={{
              width: 120,
              height: 4,
              backgroundColor: "#E4E7EC",
              borderRadius: 2,
              position: "relative",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: scrollState.thumbLeft,
                width: scrollState.thumbWidth,
                height: 4,
                backgroundColor: "#98A2B3",
                borderRadius: 2,
                transition: "left 0.15s ease",
              }}
            />
          </Box>

          {/* Right arrow */}
          <IconButton
            onClick={() => scroll("right")}
            disabled={!scrollState.canScrollRight}
            sx={{
              width: {
                sm: 24,
                md: 28,
                lg: 32,
              },
              height: {
                sm: 24,
                md: 28,
                lg: 32,
              },
              border: "1px solid",
              borderColor: scrollState.canScrollRight ? "#0066CC" : "#D0D5DD",
              color: scrollState.canScrollRight ? "#0066CC" : "#D0D5DD",
              "&:hover": {
                backgroundColor: scrollState.canScrollRight
                  ? "#EAF2FF"
                  : "transparent",
              },
              "&.Mui-disabled": {
                color: "#D0D5DD",
                borderColor: "#D0D5DD",
              },
            }}
          >
            <ChevronRightIcon sx={{ fontSize: { md: 16, lg: 20 } }} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default ChatFilter;
